import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box, Typography, Button, CircularProgress, Divider,
    FormControlLabel, Switch, ToggleButton, ToggleButtonGroup,
    Alert, Tooltip, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import Layout from './Layout';
import InvoiceStyleService from '../services/InvoiceStyleService';
import UserService from '../services/UserService';
import InvoicePreview from './settings/InvoicePreview';

const FONTS = ['Times-Roman', 'Helvetica', 'Courier'];

const Section = ({ title, children }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2c3e50', mb: 1.25 }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const FieldToggle = ({ label, checked, onChange, disabled }) => (
    <Tooltip title={disabled ? 'No data configured for this field' : ''} placement="right">
        <FormControlLabel
            control={
                <Switch
                    size="small"
                    checked={checked && !disabled}
                    onChange={(e) => !disabled && onChange(e.target.checked)}
                    disabled={disabled}
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#427898' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#427898' },
                    }}
                />
            }
            label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontSize: 13, color: disabled ? '#aaa' : '#1a2332' }}>
                        {label}
                    </Typography>
                    {disabled && (
                        <Chip label="No data" size="small" sx={{ height: 16, fontSize: '0.6rem', backgroundColor: '#f5f5f5', color: '#aaa' }} />
                    )}
                </Box>
            }
            sx={{ mb: 0.25 }}
        />
    </Tooltip>
);

export const defaultStyle = {
    headerHeight: 219,
    footerHeight: 76,
    primaryColor: '#427898',
    fontFamily: 'Times-Roman',
    logoPosition: 'LEFT',
    headerShowAddress: true,
    headerShowPhone: true,
    headerShowEmail: true,
    headerShowWebsite: false,
    footerShowAddress: false,
    footerShowPhone: false,
    footerShowEmail: false,
    footerShowWebsite: false,
    footerShowUid: true,
    footerShowBank: false,
};

export default function InvoiceStylePage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [style, setStyle]       = useState(defaultStyle);
    const [caps, setCaps]         = useState({});
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [saved, setSaved]       = useState(false);
    const [error, setError]       = useState(null);
    const [extracted, setExtracted] = useState(false);
    const [logo, setLogo]         = useState(null);
    const fileRef = useRef();

    useEffect(() => {
        InvoiceStyleService.getStyle()
            .then((res) => {
                const { systemCapabilities, ...rest } = res.data;
                setStyle(rest);
                setCaps(systemCapabilities || {});
            })
            .catch(() => {})
            .finally(() => setLoading(false));
        UserService.getCompanyInfo()
            .then((data) => setLogo(data?.logo || null))
            .catch(() => {});
    }, []);

    const set = (field, val) => {
        setStyle((prev) => ({ ...prev, [field]: val }));
        setSaved(false);
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setExtracting(true);
        setError(null);
        setExtracted(false);
        try {
            const res = await InvoiceStyleService.extractFromPdf(file);
            const { systemCapabilities, ...extracted } = res.data;
            setStyle(extracted);
            setCaps(systemCapabilities || {});
            setExtracted(true);
            setSaved(false);
        } catch {
            setError('Could not extract style from the uploaded PDF. Configure manually below.');
        } finally {
            setExtracting(false);
            e.target.value = '';
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const res = await InvoiceStyleService.saveStyle(style);
            const { systemCapabilities, ...updated } = res.data;
            setStyle(updated);
            setCaps(systemCapabilities || {});
            setSaved(true);
        } catch {
            setError('Failed to save style.');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Reset to default style?')) return;
        try {
            await InvoiceStyleService.resetStyle();
            setStyle(defaultStyle);
            setSaved(false);
            setExtracted(false);
        } catch {
            setError('Failed to reset style.');
        }
    };

    if (loading) {
        return (
            <Layout>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* Sticky page header */}
            <Box sx={{
                bgcolor: '#fff', borderBottom: '1px solid #EEF2F6',
                px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
                position: 'sticky', top: 0, zIndex: 10,
            }}>
                <Button
                    size="small"
                    startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => navigate(-1)}
                    sx={{ color: '#64748B', fontWeight: 500, textTransform: 'none', fontSize: 13, px: 1, minWidth: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
                >
                    Back
                </Button>

                <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />

                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A', lineHeight: 1.2 }}>
                        Invoice PDF Style
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
                        Upload an invoice to extract its style, or configure manually.
                    </Typography>
                </Box>

                <Box sx={{ flex: 1 }} />

                <Button
                    variant="text"
                    startIcon={<RestartAltIcon sx={{ fontSize: 15 }} />}
                    onClick={handleReset}
                    size="small"
                    sx={{ color: '#94a3b8', textTransform: 'none', fontSize: 12, flexShrink: 0 }}
                >
                    Reset
                </Button>
            </Box>

            <Box sx={{ p: 3, display: 'flex', gap: 3, alignItems: 'flex-start' }}>

                {/* ── Left: controls ──────────────────────────────────────────── */}
                <Box sx={{ flex: '0 0 340px', minWidth: 0 }}>

                    {/* Upload area */}
                    <Box
                        onClick={() => fileRef.current?.click()}
                        sx={{
                            p: 2, mb: 2.5, textAlign: 'center',
                            border: '1.5px dashed #e2e8f0', borderRadius: '8px',
                            cursor: 'pointer',
                            '&:hover': { borderColor: '#427898', backgroundColor: 'rgba(66,120,152,0.03)' },
                        }}
                    >
                        <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={handleUpload} />
                        {extracting ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <CircularProgress size={16} sx={{ color: '#427898' }} />
                                <Typography variant="body2" sx={{ color: '#64748b', fontSize: 12 }}>Analysing PDF…</Typography>
                            </Box>
                        ) : (
                            <>
                                <CloudUploadIcon sx={{ fontSize: 26, color: '#427898', mb: 0.5 }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1a2332' }}>
                                    Upload existing invoice PDF
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                                    Style will be extracted automatically
                                </Typography>
                            </>
                        )}
                    </Box>

                    {extracted && <Alert severity="success" sx={{ mb: 2, fontSize: 12 }}>Style extracted — review and save.</Alert>}
                    {error     && <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>{error}</Alert>}

                    {/* Form */}
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 2.5 }}>

                        <Section title="Dimensions">
                            <Box sx={{ display: 'flex', gap: 2.5 }}>
                                {[
                                    { label: 'Header height (pt)', field: 'headerHeight', min: 214, max: 350 },
                                    { label: 'Footer height (pt)', field: 'footerHeight', min: 56, max: 200 },
                                ].map(({ label, field, min, max }) => (
                                    <Box key={field}>
                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>{label}</Typography>
                                        <input
                                            type="number" min={min} max={max}
                                            value={style[field]}
                                            onChange={(e) => set(field, parseInt(e.target.value) || style[field])}
                                            style={{ width: 84, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none' }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Section>

                        <Divider sx={{ my: 2 }} />

                        <Section title="Visual Style">
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Primary colour</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <input
                                            type="color"
                                            value={style.primaryColor}
                                            onChange={(e) => set('primaryColor', e.target.value)}
                                            style={{ width: 36, height: 30, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', padding: 2 }}
                                        />
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{style.primaryColor}</Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Font family</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                                        {FONTS.map((f) => (
                                            <Button
                                                key={f}
                                                size="small"
                                                variant={style.fontFamily === f ? 'contained' : 'outlined'}
                                                onClick={() => set('fontFamily', f)}
                                                sx={{
                                                    fontFamily: f === 'Times-Roman' ? '"Times New Roman", serif' : f === 'Helvetica' ? 'Helvetica, Arial, sans-serif' : '"Courier New", monospace',
                                                    textTransform: 'none', fontSize: 11, py: 0.25, px: 1,
                                                    bgcolor: style.fontFamily === f ? '#427898' : 'transparent',
                                                    borderColor: style.fontFamily === f ? '#427898' : '#e2e8f0',
                                                    color: style.fontFamily === f ? '#fff' : '#64748b',
                                                    '&:hover': { bgcolor: style.fontFamily === f ? '#35617e' : '#f8fafc', borderColor: '#427898' },
                                                }}
                                            >
                                                {f === 'Times-Roman' ? 'Times' : f}
                                            </Button>
                                        ))}
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Logo position</Typography>
                                    <ToggleButtonGroup
                                        value={style.logoPosition} exclusive
                                        onChange={(_, v) => v && set('logoPosition', v)}
                                        size="small"
                                        sx={{ '& .MuiToggleButton-root.Mui-selected': { bgcolor: '#427898', color: '#fff' } }}
                                    >
                                        <ToggleButton value="LEFT" sx={{ textTransform: 'none', fontSize: 12 }}>
                                            <AlignHorizontalLeftIcon fontSize="small" />&nbsp;Left
                                        </ToggleButton>
                                        <ToggleButton value="RIGHT" sx={{ textTransform: 'none', fontSize: 12 }}>
                                            <AlignHorizontalRightIcon fontSize="small" />&nbsp;Right
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            </Box>
                        </Section>

                        <Divider sx={{ my: 2 }} />

                        <Section title="Header fields">
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                                <Box sx={{ minWidth: 150 }}>
                                    <FieldToggle label="Address" checked={style.headerShowAddress} onChange={(v) => set('headerShowAddress', v)} disabled={!caps.hasAddress} />
                                    <FieldToggle label="Phone"   checked={style.headerShowPhone}   onChange={(v) => set('headerShowPhone', v)}   disabled={!caps.hasPhone} />
                                </Box>
                                <Box sx={{ minWidth: 150 }}>
                                    <FieldToggle label="Email"   checked={style.headerShowEmail}   onChange={(v) => set('headerShowEmail', v)}   disabled={!caps.hasEmail} />
                                    <FieldToggle label="Website" checked={style.headerShowWebsite} onChange={(v) => set('headerShowWebsite', v)} disabled={!caps.hasWebsite} />
                                </Box>
                            </Box>
                        </Section>

                        <Divider sx={{ my: 2 }} />

                        <Section title="Footer fields">
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                                <Box sx={{ minWidth: 150 }}>
                                    <FieldToggle label="Address" checked={style.footerShowAddress} onChange={(v) => set('footerShowAddress', v)} disabled={!caps.hasAddress} />
                                    <FieldToggle label="Phone"   checked={style.footerShowPhone}   onChange={(v) => set('footerShowPhone', v)}   disabled={!caps.hasPhone} />
                                    <FieldToggle label="Email"   checked={style.footerShowEmail}   onChange={(v) => set('footerShowEmail', v)}   disabled={!caps.hasEmail} />
                                </Box>
                                <Box sx={{ minWidth: 150 }}>
                                    <FieldToggle label="Website"         checked={style.footerShowWebsite} onChange={(v) => set('footerShowWebsite', v)} disabled={!caps.hasWebsite} />
                                    <FieldToggle label="UID / Tax number" checked={style.footerShowUid}    onChange={(v) => set('footerShowUid', v)}     disabled={!caps.hasUid} />
                                    <FieldToggle label="Bank / IBAN"     checked={style.footerShowBank}    onChange={(v) => set('footerShowBank', v)}     disabled={!caps.hasBank} />
                                </Box>
                            </Box>
                        </Section>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                                onClick={handleSave}
                                disabled={saving}
                                sx={{ backgroundColor: '#427898', '&:hover': { backgroundColor: '#35617e' }, textTransform: 'none', fontWeight: 600 }}
                            >
                                {saved ? 'Saved ✓' : 'Save style'}
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* ── Right: live preview (sticky) ────────────────────────────── */}
                <Box sx={{ flex: 1, minWidth: 0, position: 'sticky', top: 24 }}>
                    <InvoicePreview style={style} logo={logo} />
                    <Typography sx={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', mt: 1.5 }}>
                        {t('invoiceStyle.appliesTo', 'This style is applied to invoices, reports, quotes and credit notes.')}
                    </Typography>
                </Box>
            </Box>
        </Layout>
    );
}
